import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Clock, Video, Copy, Check, MessageCircle } from "lucide-react";
import { WHATSAPP, whatsappUrl, DOCTOR, CONTACT, BRAND } from "@/lib/constants";

const steps = [
  {
    icon: ArrowUpRight,
    title: "Tap the button",
    body: "WhatsApp opens with a pre-filled message. Send it — no forms to complete.",
  },
  {
    icon: Clock,
    title: "Receive a slot",
    body: "The clinic replies with the earliest secure teleconsultation or in-person windows, usually within 4 working hours.",
  },
  {
    icon: Video,
    title: "Join securely",
    body: "You'll receive a private, encrypted video link and a report-review checklist ahead of the consult.",
  },
];

export const BookingWhatsApp = ({ heading = true }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(WHATSAPP.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* no-op */
    }
  };

  return (
    <section
      data-testid="booking-section"
      className="relative bg-white overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        {heading && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-8">
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
                WhatsApp Booking Engine
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A2540]">
                A single tap.
                <br />
                <span className="italic text-[#2E5AA5]">A private consult.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="font-sans text-base text-[#475569] leading-relaxed">
                Booking is handled on WhatsApp for speed, continuity, and zero
                form friction. Send one message — {BRAND.name} responds with
                available slots and a secure video link or in-person
                appointment.
              </p>
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-[#0A2540] text-white p-8 md:p-14 lg:p-20 overflow-hidden"
        >
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#B08A4A]/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-[#2E5AA5]/15 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
                Consult with — {DOCTOR.name}
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
                Book Consultation / Report Query
                <br />
                <span className="italic text-[#B08A4A]">via WhatsApp</span>
              </h2>
              <p className="font-sans text-base text-white/75 leading-relaxed mb-10 max-w-xl">
                A pre-composed message will open in WhatsApp. You can edit it,
                attach reports if you wish, and send. The clinic replies with
                available slots and secure access details.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-book-btn"
                  className="group inline-flex items-center justify-center gap-3 bg-white text-[#0A2540] hover:bg-[#B08A4A] hover:text-white px-10 py-5 text-xs tracking-widest-plus uppercase transition-all duration-300 border border-white font-semibold"
                >
                  <MessageCircle size={18} strokeWidth={1.5} />
                  Book via WhatsApp
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.5}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>

                <button
                  onClick={handleCopy}
                  data-testid="copy-message-btn"
                  className="inline-flex items-center justify-center gap-3 bg-transparent text-white border border-white/40 hover:border-white hover:bg-white/10 px-8 py-5 text-xs tracking-widest-plus uppercase transition-all duration-300"
                >
                  {copied ? (
                    <>
                      <Check size={14} strokeWidth={1.5} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={14} strokeWidth={1.5} />
                      Copy Message
                    </>
                  )}
                </button>
              </div>

              <div className="mt-10 flex items-start gap-3 text-white/70">
                <ShieldCheck size={16} strokeWidth={1.5} className="mt-0.5 text-[#B08A4A]" />
                <p className="font-sans text-xs leading-relaxed max-w-md">
                  Your message is sent directly to the clinic. No form data is
                  stored on this website. Video consultations use a private
                  end-to-end encrypted link sent after slot confirmation.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div
                data-testid="message-preview"
                className="bg-white/[0.04] backdrop-blur-sm border border-white/15 p-6"
              >
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/15">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest-plus text-white/50">
                      To — WhatsApp
                    </div>
                    <div className="font-serif text-lg text-white">
                      {BRAND.name}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] text-white/60">
                    {CONTACT.phone}
                  </div>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest-plus text-[#B08A4A] mb-2">
                  Pre-filled message
                </div>
                <p className="font-sans text-[15px] text-white leading-[1.75]">
                  &ldquo;{WHATSAPP.message}&rdquo;
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`booking-step-${i}`}
                className="border-t border-[#0A2540] pt-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-xs text-[#B08A4A] tracking-widest-plus">
                    Step {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon size={16} strokeWidth={1.5} className="text-[#0A2540]" />
                </div>
                <h3 className="font-serif text-2xl text-[#0A2540] leading-tight mb-3">
                  {s.title}
                </h3>
                <p className="font-sans text-sm text-[#475569] leading-relaxed">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-20 border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-2">
                Not on WhatsApp?
              </div>
              <p className="font-serif text-2xl text-[#0A2540] leading-snug">
                Email us at{" "}
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="italic text-[#2E5AA5] underline decoration-[#B08A4A] underline-offset-4 hover:decoration-[#0A2540]"
                  data-testid="booking-email-link"
                >
                  {CONTACT.email}
                </a>{" "}
                and a scheduler will respond during clinic hours.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-1">
                Clinic Location
              </div>
              <div className="font-serif text-lg text-[#0A2540]">
                {CONTACT.address.line1}, {CONTACT.address.line2}, Gwalior
              </div>
              <div className="font-mono text-[10px] text-[#475569] mt-1">
                Mon — Sat · 8:00 AM — 10:00 PM · Sun · 8:00 AM — 5:00 PM
              </div>
              <div
                data-testid="booking-appointment-note"
                className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mt-1.5"
              >
                {CONTACT.appointmentNote}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingWhatsApp;
